import { Component, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

/**
 * Keeps a crashing view from taking the whole app down with it. Without this, an
 * uncaught render error unmounts everything — including the sidebar nav — and the
 * page goes blank. Here the routed content is caught and a message shown in its
 * place; the nav stays, so you can move to another page. Key it on the route so it
 * resets on navigation.
 */
export class ViewErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    console.error('View crashed:', error);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="panel" style={{ margin: 16, borderLeft: '3px solid var(--bad)' }}>
          <strong style={{ color: 'var(--bad)' }}>Something went wrong on this page.</strong>
          <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>{this.state.error.message}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            The rest of the app is fine — use the menu to go to another page.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
